import React, { useEffect } from 'react';
import _ from 'lodash';
import { Box, Link, Stack, Button, Divider, Typography } from '@mui/material';
import { EyeIcon, ShareIcon, ThumbsupFullGreyIcon } from '../../../../../utils/Icons';
import { topicArray, industryArray, formatNumberWithCommas } from '../../../components/common';
import AudioPlayer from './AudioPlayer';
import { IArticleData } from '../LibraryTab';
import TagBox from '../../../components/TagBox';
import MyChip from '../../../components/MyChip';
import { FileOutlined } from '@ant-design/icons';
import HelpConcern from '../../Help/HelpConcern';
import { LibraryRightBarLoader } from './LibraryLoader';
import { CommonStyle } from '../../../../../utils/muiStyle';
import RenderHTML from '../../../../../components/RenderHTML';
import { useUserRole } from '../../../../../utils/useUserRole';
import ChatAvatarComponent from '../../chat/ChatAvatarComponent';
import { useXpPoints } from '../../../components/menteeXpPoints';
import handthumbsup from './../../../../../assets/images/hand-thumbs-up.svg';
import handthumbsdown from './../../../../../assets/images/hand-thumbs-down.svg';
import handthumbsupfill from './../../../../../assets/images/hand-thumbs-up-fill.svg';
import handthumbsdownfill from './../../../../../assets/images/hand-thumbs-down-fill.svg';

interface LibraryDetailsProps {
  articleDetails: IArticleData | undefined;
  selectedArticleId: string;
  handleLikeUp: () => Promise<void>;
  handleLikeDown: () => Promise<void>;
  likeStatus: string;
  isVoted: any;
  loading: boolean;
  loadingDown: boolean;
  isconcernData?: boolean;
  articleDetailsLoading: boolean;
}

const LibraryDetails = ({
  articleDetails,
  handleLikeUp,
  handleLikeDown,
  likeStatus,
  isVoted,
  loading,
  loadingDown,
  isconcernData,
  articleDetailsLoading
}: LibraryDetailsProps) => {
  const classes = CommonStyle();
  const getXpPoints = useXpPoints();
  const { isMentee } = useUserRole();

  // Get the mentee's Xp Points
  useEffect(() => {
    if (isMentee) {
      getXpPoints('mentee');
    }
  }, [articleDetails]);

  if (articleDetailsLoading) {
    return <LibraryRightBarLoader />;
  }

  // If articleDetails is undefined, return null
  if (isconcernData) {
    return <HelpConcern />;
  }

  // If articleDetails is not available, return null
  const topics = topicArray();
  const industries = industryArray();
  const camelCaseToCapital = (topicsArray: string[]) => {
    return topicsArray.map((tag: string) => {
      const index = topics.findIndex((each) => each.name === tag);
      if (index !== -1) {
        return topics[index].label;
      } else {
        return tag;
      }
    });
  };

  // Convert camelCase industries to capitalized labels
  const camelCaseToCapitalIndustries = (industriesArray: string[]) => {
    return industriesArray.map((tag: string) => {
      const index = industries.findIndex((each) => each.name === tag);
      if (index !== -1) {
        return industries[index].label;
      } else {
        return tag;
      }
    });
  };

  return (
    <>
      {articleDetails && (
        <Box p={2} sx={{ fontFamily: 'Inter, sans-serif', color: '#FDFDFD', background: '#000' }}>
          {/* Render article title */}
          <Typography variant="h3">{articleDetails?.articleName}</Typography>
          {/* Render article topic and access type */}
          {articleDetails.accessFor !== 'Public' && <TagBox text="Network Exclusive Content" marginTop={0.8} />}
          {/* Render article creation date */}
          <Typography mt={1}>
            {articleDetails?.createdAt && (
              <span>
                {new Date(articleDetails.createdAt * 1000).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            )}
            &nbsp; • &nbsp;{articleDetails?.readTime} read
          </Typography>
          {/* Render article tags and industries */}
          <Box my={1}>
            {articleDetails && (
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                {articleDetails?.tags &&
                  articleDetails?.industries &&
                  camelCaseToCapital(articleDetails?.tags)
                    .concat(camelCaseToCapitalIndustries(articleDetails?.industries))
                    .map((ele: any, index: number) => (
                      <React.Fragment key={ele}>
                        <MyChip label={ele} color="#68717A" backgroundColor="#EFF0F4" />
                      </React.Fragment>
                    ))}
              </Stack>
            )}
          </Box>

          {/* Render article author */}
          <Stack direction="row" alignItems="center" spacing={1} mt={2}>
            <Box>
              <ChatAvatarComponent
                image={articleDetails?.coverImageUrl}
                firstLetter={articleDetails?.authorName?.slice(0, 1)}
                width="28px"
                height="28px"
                cursor="pointer"
                type="noStatus"
              />
            </Box>
            <Box>
              <Typography color="primary">{articleDetails?.authorName}</Typography>
            </Box>
          </Stack>

          <Divider className="my-20" />

          {/* Render article media */}
          <Box data-testid="article-detail-content">
            <Box mb={2}>
              {/* Image */}
              {/* {articleDetails?.type === 'Text' && (
                <img src={articleDetails?.coverImageUrl} alt="resumeimg" className="LibraryImage" />
              )} */}

              {/* Video */}
              {articleDetails?.type === 'Video' && (
                <Box>
                  <iframe
                    title="LibraryVideo"
                    className="LibraryVideoAndImage"
                    src={articleDetails?.fileUrl} // url for the video file
                    frameBorder="0"
                    // allowFullScreen
                  />
                </Box>
              )}

              {/* Audio */}
              {articleDetails?.type === 'Audio' && <AudioPlayer src={articleDetails?.fileUrl} />}
            </Box>

            {/* Url or file */}
            {articleDetails?.type === 'Text' && articleDetails?.fileUrl && _.size(articleDetails?.fileUrl) && (
              <Box mt={2}>
                <Link
                  onClick={() =>
                    articleDetails?.fileUrl?.includes('http') ? window.open(articleDetails?.fileUrl, '_blank') : {}
                  }
                  className="color-blue"
                >
                  <FileOutlined /> {articleDetails?.articleName}
                </Link>
              </Box>
            )}

            {/* Render article description */}
            <Box mt={2}>
              <RenderHTML htmlContent={articleDetails?.articleDescription} />
            </Box>
          </Box>

          {/* Embedded links */}
          {articleDetails?.embeddedLinks && (
            <Box mt={2}>
              {articleDetails.embeddedLinks.split(',').map((link, index) => {
                const trimmedLink = link.trim();
                const extension = trimmedLink.split('.').pop()?.toLowerCase();

                if (extension === 'jpg' || extension === 'jpeg' || extension === 'png') {
                  return (
                    <Box key={index}>
                      <img src={trimmedLink} alt={`Embedded Image ${index}`} className="LibraryVideoAndImage" />
                    </Box>
                  );
                } else {
                  return (
                    <Box key={index}>
                      <iframe
                        title={`Embedded Document ${index}`}
                        src={trimmedLink}
                        // className="LibaryEmbeddedDocument"
                        // allowFullScreen
                      />
                    </Box>
                  );
                }
              })}
            </Box>
          )}

          {/* Like, Dislike, View, Share count buttons */}
          <Stack direction="row" alignItems="center" spacing={1} mt={2}>
            <Button className={classes.LikeViewShareButton} disabled>
              <ThumbsupFullGreyIcon width={12} height={15} /> {formatNumberWithCommas(articleDetails?.upVotes)}
            </Button>
            <Button className={classes.LikeViewShareButton} disabled>
              <ThumbsupFullGreyIcon width={12} height={15} style={{ transform: 'rotate(180deg)' }} />
              {formatNumberWithCommas(articleDetails?.downVotes)}
            </Button>
            <Button className={classes.LikeViewShareButton} data-testid="article-view-count" disabled>
              <EyeIcon size={17} /> {formatNumberWithCommas(articleDetails?.views)}
            </Button>
            {/* <Button className={classes.LikeViewShareButton}>
              <ShareIcon size={13} /> Share
            </Button> */}
          </Stack>

          <Divider className="my-20" />

          {/* Was this content helpful */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="primary">Was this content helpful?</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Like */}
              {likeStatus === 'upVote' ? (
                loading ? (
                  <img src={handthumbsupfill} className="cursor-pointer" alt="hand-thumbs-down-fill" />
                ) : (
                  <img
                    src={handthumbsupfill}
                    onClick={handleLikeUp}
                    className="cursor-pointer"
                    alt="hand-thumbs-down-fill"
                  />
                )
              ) : loading ? (
                <img src={handthumbsup} alt="hand-thumbs-down-fill" className="cursor-pointer opacity-25" />
              ) : (
                <img
                  src={handthumbsup}
                  onClick={handleLikeUp}
                  alt="hand-thumbs-down-fill"
                  className="cursor-pointer opacity-25"
                />
              )}

              {/* Dislike */}
              {likeStatus === 'downVote' ? (
                loadingDown ? (
                  <img src={handthumbsdownfill} className="cursor-pointer" alt="hand-thumbs-down-fill" />
                ) : (
                  <img
                    src={handthumbsdownfill}
                    onClick={handleLikeDown}
                    className="cursor-pointer"
                    alt="hand-thumbs-down-fill"
                  />
                )
              ) : loadingDown ? (
                <img src={handthumbsdown} alt="hand-thumbs-down-fill" className="cursor-pointer opacity-25" />
              ) : (
                <img
                  src={handthumbsdown}
                  onClick={handleLikeDown}
                  alt="hand-thumbs-down-fill"
                  className="cursor-pointer opacity-25"
                />
              )}
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default LibraryDetails;
